import { useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'planche_label_settings';

/**
 * 라벨 설정 관련 상태 및 로직을 관리하는 커스텀 훅
 * - 위치 (tl, tr, bl, br, custom)
 * - 커스텀 위치 좌표
 * - 폰트, 굵기, 크기
 * - localStorage 자동 저장/로드
 */
const useLabelSettings = () => {
    const [labelPosition, setLabelPosition] = useState('tl');
    const [customLabelPos, setCustomLabelPos] = useState({ x: 10, y: 10 });
    const [isDraggingLabel, setIsDraggingLabel] = useState(false);
    const [labelFont, setLabelFont] = useState('sans-serif');
    const [labelWeight, setLabelWeight] = useState('300');
    const [labelSize, setLabelSize] = useState('14');

    // 초기 로드: localStorage에서 설정 불러오기
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const label = JSON.parse(saved);
                if (label.position) setLabelPosition(label.position);
                if (label.customPos) setCustomLabelPos(label.customPos);
                if (label.font) setLabelFont(label.font);
                if (label.weight) setLabelWeight(label.weight);
                if (label.size) setLabelSize(label.size);
            } catch (e) {
                console.error('Failed to parse label settings', e);
            }
        }
    }, []);

    // 설정 변경 시 localStorage에 저장
    useEffect(() => {
        const labelSettings = {
            position: labelPosition,
            customPos: customLabelPos,
            font: labelFont,
            weight: labelWeight,
            size: labelSize,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(labelSettings));
    }, [labelPosition, customLabelPos, labelFont, labelWeight, labelSize]);

    // 일괄 업데이트
    const updateLabelSettings = useCallback((updates) => {
        if (updates.position !== undefined) setLabelPosition(updates.position);
        if (updates.customPos !== undefined) setCustomLabelPos(updates.customPos);
        if (updates.font !== undefined) setLabelFont(updates.font);
        if (updates.weight !== undefined) setLabelWeight(updates.weight);
        if (updates.size !== undefined) setLabelSize(updates.size);
    }, []);

    // 라벨 스타일 객체 (메모이제이션)
    const labelStyle = useMemo(() => ({
        position: labelPosition,
        customPos: customLabelPos,
        font: labelFont,
        weight: labelWeight,
        size: labelSize,
    }), [labelPosition, customLabelPos, labelFont, labelWeight, labelSize]);

    // 위치 프리셋에 따른 실제 좌표 계산
    const getLabelCoordinates = useCallback((canvasWidth, canvasHeight) => {
        const padding = 10;
        switch (labelPosition) {
            case 'tl':
                return { x: padding, y: padding };
            case 'tr':
                return { x: canvasWidth - padding, y: padding };
            case 'bl':
                return { x: padding, y: canvasHeight - padding };
            case 'br':
                return { x: canvasWidth - padding, y: canvasHeight - padding };
            case 'custom':
            default:
                return customLabelPos;
        }
    }, [labelPosition, customLabelPos]);

    return {
        // 상태
        labelPosition,
        customLabelPos,
        isDraggingLabel,
        labelFont,
        labelWeight,
        labelSize,

        // 개별 setter
        setLabelPosition,
        setCustomLabelPos,
        setIsDraggingLabel,
        setLabelFont,
        setLabelWeight,
        setLabelSize,

        // 유틸리티
        updateLabelSettings,
        labelStyle,
        getLabelCoordinates,
    };
};

export default useLabelSettings;
